import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { GroupService } from '../../../../../services/group.service';
import { CounselorStudent } from '../../../../../services/student.service';
import { switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Group } from '../../../../../models/group';
import { MatIcon } from '@angular/material/icon';
import { MatSelectTrigger } from '@angular/material/select';

export interface GroupDialogData {
  group?: Group;
  students: CounselorStudent[];
}

export interface GroupDialogResult {
  created?: boolean;
  updated?: Group;
}
export interface GroupIconOption {
  value: string;
  label: string;
}

export const GROUP_ICONS: GroupIconOption[] = [
  { value: 'biotech', label: 'Science' },
  { value: 'functions', label: 'Math' },
  { value: 'menu_book', label: 'English / Language Arts' },
  { value: 'history_edu', label: 'History / Social Studies' },
  { value: 'language', label: 'World Languages' },
  { value: 'computer', label: 'Computer Science' },
  { value: 'sports_soccer', label: 'Sports' },
  { value: 'palette', label: 'Visual Art' },
  { value: 'music_note', label: 'Music' },
  { value: 'theater_comedy', label: 'Theater / Drama' },
  { value: 'groups', label: 'General Club' },
  { value: 'diversity_3', label: 'Leadership / Student Council' },
  { value: 'volunteer_activism', label: 'Community Service' },
  { value: 'rocket_launch', label: 'STEM / Robotics' },
  { value: 'school', label: 'Other' },
];

@Component({
  selector: 'app-group-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIcon,
    MatSelectTrigger,
  ],
  templateUrl: './group-dialog.html',
  styleUrl: './group-dialog.css',
})
export class GroupDialog {
  dialogRef = inject(MatDialogRef<GroupDialog, GroupDialogResult>);
  data = inject<GroupDialogData>(MAT_DIALOG_DATA);
  private groupService = inject(GroupService);

  icons = GROUP_ICONS;

  isEdit = !!this.data.group;

  form = new FormGroup({
    name: new FormControl(this.data.group?.name ?? '', {
      nonNullable: true,
      validators: Validators.required,
    }),
    description: new FormControl(this.data.group?.description ?? '', { nonNullable: true }),
    icon: new FormControl(this.data.group?.icon ?? 'General Club', {
      nonNullable: true,
    }),
    memberIds: new FormControl<number[]>(this.data.group?.members.map((u) => u.id) ?? [], {
      nonNullable: true,
    }),
  });

  get availableStudents(): CounselorStudent[] {
    return this.data.students;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();

    if (this.isEdit && this.data.group) {
      this.groupService
        .updateGroup(this.data.group.id, {
          name: raw.name,
          description: raw.description,
          icon: raw.icon,
        })
        .subscribe({
          next: (updated) => this.dialogRef.close({ updated }),
          error: (err) => console.error('updateGroup failed:', err),
        });
    } else {
      this.groupService
        .createGroup({ name: raw.name, description: raw.description, icon: raw.icon })
        .pipe(
          switchMap((group) => {
            if (!raw.memberIds.length) return of(group);
            const additions = raw.memberIds.map((userId) =>
              this.groupService.addGroupMember(group.id, userId),
            );
            return forkJoin(additions).pipe(switchMap(() => of(group)));
          }),
        )
        .subscribe({
          next: () => this.dialogRef.close({ created: true }),
          error: (err) => console.error('createGroup failed:', err),
        });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
